import { AddressModel, AddressSubType, AddressSubTypeLowercase, AddressType } from '@core/address';
import {
  AREA_CODE,
  AreaCodePhoneNumber,
  BaseModel,
  ClonedIsoString,
  CountryModel,
  CrsTaxResidenceModel,
  DATEPICKER,
  ESubmissionMethod,
  PersonnelAppointment,
  PersonnelConfirmStatus,
  PersonnelGender,
  PersonnelMarital,
  PersonnelOwnership,
  PersonnelRace,
  PersonnelRelationship,
  PersonnelResidence,
  PersonnelResidency,
  PersonnelType,
  ToStartOfLocalDateString,
  UniqueArray,
} from '@core/data-access';
import { FlattenObject, IMyInfo, IMyInfoAddress } from '@core/singpass';
import { DeepPartial, Default, Helper } from '@core/util';
import { Exclude, Expose, Transform, TransformFnParams, Type } from 'class-transformer';
import { mapGender, mapResidentialStatus } from '../util';
import { v4 } from 'uuid';
import { ObjectNumber, PersonnelEntityState } from '../type';
import { AttachmentModel } from './attachment.model';
import { PersonnelAddressModel } from './personnel-address.model';
import { PERSONNEL_OTHER } from '../const/app.const';
import { HdbOwnershipPropertyModel } from './hdb-ownership.model';
import { CpfContributionHistoryModel } from './cpf-contribution.model';
import { NoticeOfAssessmentModel } from './noa.model';
import { CpfEmployerHistoryModel } from './cpf-employer.model';

@Exclude()
export class PersonnelFatcaModel extends BaseModel {
  @Expose() usPermanentResidentCardHolder!: boolean;

  @Expose() usPermanentResidentCardHolderTaxNumber!: string;

  @Expose() visa!: string;
}

@Exclude()
export class PersonnelCreditBureauSingaporeModel {
  @Expose()
  @Default(null)
  reportDate!: string | null;

  @Expose()
  @ToStartOfLocalDateString(DATEPICKER.PATTERN)
  @Default(null)
  reportDateOnly!: string | null;

  @Expose() enquiryNumber!: string;

  @Expose()
  @Default(null)
  dateOfEarliestKnownCreditAccount!: string | null;

  @Expose()
  @ToStartOfLocalDateString(DATEPICKER.PATTERN)
  @Default(null)
  dateOfEarliestKnownCreditAccountOnly!: string | null;

  @Expose() previousEnquiries!: string;

  @Expose() accounts!: string;

  @Expose() defaults!: string;

  @Expose() bankruptcyProceedings!: number;

  @Expose() securedCreditLimit!: number;

  @Expose() unsecuredCreditLimit!: number;

  @Expose() exemptedCreditLimit!: number;

  @Expose() riskGradeDescription!: string;

  @Expose() aggregatedMonthlyInstalment!: number;

  @Expose() creditReportRequest!: boolean;
}

export class AddressMetadataModel extends BaseModel {
  @Expose() haveForeignAddress?: boolean; // why this property always is false? => true when "Foreign Address (If have)" selected

  /**
   * To distinguish between a Business Address and a Personal Address
   *
   * Always be true when is Personal Address
   */
  @Expose() showForeignAddress?: boolean;

  @Expose() maillingAddressType?: AddressSubTypeLowercase; // must be update based on AddressModel.mailing
  @Expose() maillingAddressDifferent?: boolean; // must be update based on AddressModel.differentResidential
}

@Exclude()
export class PersonnelMetadataModel extends BaseModel {
  @Expose()
  @Default(() => AddressMetadataModel.createEmpty())
  addressMetadata!: AddressMetadataModel;

  @Expose()
  @Default(() => v4())
  uuid!: string;

  @Expose()
  order!: number;
}

@Exclude()
export class PersonnelContactModel extends BaseModel {
  @Expose() @AreaCodePhoneNumber() @Default(AREA_CODE.DEFAULT) contactAreaCode!: string;

  @Expose() contactNumber!: string;

  @Expose() email!: string;
}

@Exclude()
export class BasePersonnelModel extends PersonnelContactModel {
  /**
   * Primary ID
   *
   * Null when create
   */
  @Expose() @Default(null) id!: number | null;

  @Expose() name!: string;

  @Expose() nricNumber!: string;

  @Expose() gender!: PersonnelGender;

  @Expose()
  @Default(null)
  dateOfBirth!: string | null;

  @Expose()
  @ToStartOfLocalDateString(DATEPICKER.PATTERN)
  @Default(null)
  dateOfBirthOnly!: string | null;

  @Expose()
  @Type(() => PersonnelMetadataModel)
  @Default(() => PersonnelMetadataModel.createEmpty())
  @Transform((params: TransformFnParams) => {
    const address = params.obj?.['address'] ? PersonnelAddressModel.fromJson(params.obj?.['address']) : PersonnelAddressModel.createEmpty();
    const value = params.value ? PersonnelMetadataModel.fromJson(params.value) : PersonnelMetadataModel.createEmpty();
    // if (address) {
    //   value.addressMetadata.maillingAddressDifferent = address.differentResidential;
    // }
    return value;
  })
  metadata!: PersonnelMetadataModel;

  @Expose({ toClassOnly: true })
  @Default([])
  @Type(() => AttachmentModel)
  attachments!: AttachmentModel[];

  @Expose()
  @Default([])
  @Type(() => AddressModel)
  addresses!: AddressModel[];

  @Expose({ toClassOnly: true })
  @Type(() => PersonnelAddressModel)
  address!: PersonnelAddressModel;
}

@Exclude()
export class PersonnelModel extends BasePersonnelModel implements InstanceType<typeof PersonnelFatcaModel> {
  @Expose() @Default(ESubmissionMethod.MANUAL) method!: ESubmissionMethod;

  /**
   * Using in Select Personnel Option
   *
   * Value in UUID v4 or PERSONNEL_OTHER
   *
   * TODO:
   * - move to BasePersonnel after cover this in BeneficialOwnerModel & UsPersonModel
   */
  @Expose({ toClassOnly: true })
  @Default(null)
  uuid!: typeof PERSONNEL_OTHER | string | null;

  /**
   * Using for patching self data instead of using CorpPass Personnel
   */
  @Expose({ toClassOnly: true }) @Default(null) self!: DeepPartial<InstanceType<typeof PersonnelModel>> | null;

  /**
   * Using Personnel Store
   */
  @Expose({ toClassOnly: true }) @Default(null) entityId!: string | null;

  /**
   * Using Personnel Store
   */
  @Expose({ toClassOnly: true }) @Default(null) entityState!: PersonnelEntityState;

  @Expose({ toClassOnly: true }) type!: PersonnelType;

  @Expose() @Default([]) @UniqueArray() appointment!: PersonnelAppointment[];

  @Expose() @Type(() => Boolean) @Default(null) isReviewedBoardResolution!: boolean | null;

  @Expose()
  @Default(null)
  dateOfAppointment!: string | null;

  @Expose()
  @ToStartOfLocalDateString(DATEPICKER.PATTERN)
  @Default(null)
  dateOfAppointmentOnly!: string | null;

  @Expose() share!: number;

  @Expose() nationalityId!: number;

  @Expose() residentialStatus!: PersonnelResidency;

  @Expose() residencyId!: number;

  @Expose() placeOfBirthCountryId!: number;

  @Expose() placeOfBirthProvinceId!: number;

  @Expose() placeOfBirthCityId!: number;

  @Expose() @Default([]) relationshipToApplicant!: PersonnelRelationship[];

  @Expose() relationshipToApplicantOther!: string;

  @Expose() typeOfResidence!: PersonnelResidence;

  @Expose() lengthOfStay!: number;

  @Expose() @AreaCodePhoneNumber() residentialAreaCode!: string;

  @Expose() residentialNumber!: string;

  @Expose() @AreaCodePhoneNumber() officeAreaCode!: string;

  @Expose() officeNumber!: string;

  @Expose() race!: PersonnelRace;

  @Expose() raceOther!: string;

  @Expose() yearsOfExperience!: number;

  @Expose() ownership!: PersonnelOwnership;

  @Expose() ownershipOther!: string;

  @Expose() rentalAmount!: number;

  @Expose() dualNationality!: boolean;

  @Expose() dualNationalityId!: number;

  @Expose() usResident!: boolean;

  @Expose() usResidentTaxNumber!: string;

  @Expose() usCitizen!: boolean;

  @Expose() usCitizenTaxNumber!: string;

  @Expose() annualIncomeLatestYear!: number;

  @Expose() annualIncome!: number;

  @Expose() tradeIncomeLatestYear!: number;

  @Expose() tradeIncome!: number;

  @Expose()
  @Type(() => PersonnelCreditBureauSingaporeModel)
  creditBureauSingapore!: PersonnelCreditBureauSingaporeModel;

  @Expose()
  @Default([])
  @Type(() => CrsTaxResidenceModel)
  crs!: CrsTaxResidenceModel[];

  // @Expose() isContactEmail!: boolean;
  //
  // @Expose() isContactSMS!: boolean;
  //
  // @Expose() isEAdvice!: boolean;

  @Expose() maritalStatus!: PersonnelMarital;

  @Expose() remark!: string;

  @Expose() usPermanentResidentCardHolder!: boolean;

  @Expose() usPermanentResidentCardHolderTaxNumber!: string;

  @Expose() visa!: string;

  @Expose({ toClassOnly: true }) confirmationStatus!: PersonnelConfirmStatus;

  // @Expose({ toClassOnly: true }) @Default(null) cpfContributionHistory!: DeepPartial<CPFContributionHistory[]> | null;
  // @Expose({ toClassOnly: true }) @Default(null) noticeOfAssessmentHistory!: DeepPartial<NoticeOfAssessmentHistory[]> | null;

  @Expose() aliasName!: string;

  @Expose() hanyuPinyinName!: string;

  @Expose() hanyuPinyinAliasName!: string;

  @Expose() passportNumber!: string;

  @Expose()
  @ClonedIsoString('passportExpiryDateOnly')
  passportExpiryDate!: string;

  @Expose() passportExpiryDateOnly!: string;

  @Expose() passType!: string;

  @Expose() passStatus!: string;

  @Expose()
  @ClonedIsoString('passExpiryDateOnly')
  passExpiryDate!: string;

  @Expose() passExpiryDateOnly!: string;

  @Expose() hdbType!: string;

  @Expose() housingType!: string;

  @Expose()
  @ClonedIsoString('marriageDateOnly')
  marriageDate!: string;

  @Expose() marriageDateOnly!: string;

  @Expose()
  @ClonedIsoString('divorceDateOnly')
  divorceDate!: string;

  @Expose() divorceDateOnly!: string;

  @Expose() countryOfMarriageId!: number;

  @Expose() ownerPrivate!: boolean;

  @Expose()
  @FlattenObject('hdbOwnership.properties')
  @Type(() => HdbOwnershipPropertyModel)
  hdbOwnership?: HdbOwnershipPropertyModel[];

  @Expose()
  @FlattenObject('cpfEmployers.history')
  @Type(() => CpfEmployerHistoryModel)
  cpfEmployers?: CpfEmployerHistoryModel[];

  @Expose()
  @FlattenObject('cpfHousingWithdrawal.withdrawals')
  @Type(() => CpfEmployerHistoryModel)
  cpfHousingWithdrawal?: CpfEmployerHistoryModel[];

  @Expose()
  @FlattenObject('cpfContributions.history')
  @Type(() => CpfContributionHistoryModel)
  cpfContributions?: CpfContributionHistoryModel[];

  @Expose()
  @FlattenObject('noaHistory.noas')
  @Type(() => NoticeOfAssessmentModel)
  noaHistory?: NoticeOfAssessmentModel[];

  assignDataFromSingpass(args: { myinfo: IMyInfo; countries: CountryModel[]; singpassMap: ObjectNumber }): PersonnelModel {
    const { myinfo, countries } = args;
    const personnel = PersonnelModel.clone(this);

    this.assignBasicInfo(personnel, myinfo);
    this.assignAddressInfo(personnel, myinfo, countries);
    this.assignAdditionalInfo(personnel, args);

    return personnel;
  }

  private assignBasicInfo(personnel: PersonnelModel, myinfo: IMyInfo): void {
    personnel.name = myinfo.name?.value ?? '';
    personnel.nricNumber = myinfo.uinfin?.value ?? '';
    personnel.dateOfBirthOnly = myinfo.dob?.value ?? '';
    personnel.email = myinfo.email?.value ?? '';
  }

  private assignAddressInfo(personnel: PersonnelModel, myinfo: IMyInfo, countries: CountryModel[]): void {
    const { regadd, mailadd } = myinfo;
    personnel.address ??= PersonnelAddressModel.createEmpty();

    if (regadd) {
      const residential = personnel.address.residential;
      residential.type = AddressType.RESIDENTIAL_ADDRESS;
      this.assignPersonnelAddressInfo({ address: residential, infoadd: regadd, countries });
    }

    if (mailadd) {
      const mailing = personnel.address.mailing;
      mailing.type = AddressType.MAILING_ADDRESS;
      personnel.address.differentResidential = true;
      this.assignPersonnelAddressInfo({ address: mailing, infoadd: mailadd, countries });
    }
  }

  private assignPersonnelAddressInfo(data: { address: AddressModel; infoadd: IMyInfoAddress; countries: CountryModel[] }): void {
    const { address, infoadd, countries } = data;
    if (infoadd.type === 'SG') {
      address.subType = AddressSubType.LOCAL;
      address.blockNumber = infoadd.block?.value || '';
      address.buildingName = infoadd.building?.value || '';
      address.streetName = infoadd.street?.value || '';
      address.unitNumber = infoadd.unit?.value || '';
      address.postalCode = infoadd.postal?.value || '';
      address.storey = infoadd.floor?.value || '';

      const country = countries.find(item => item.alpha2 === infoadd.country?.code || 'SG');
      country && (address.country = country);
    }

    if (infoadd.type === 'UNFORMATTED') {
      address.subType = AddressSubType.FOREIGN;
      address.blockNumber = '';
      address.buildingName = '';
      address.streetName = '';
      address.unitNumber = '';
      address.postalCode = infoadd.postal?.value || '';
      address.addressLine1 = infoadd.line1?.value || '';
      address.addressLine2 = infoadd.line2?.value || '';
    }
  }

  private assignAdditionalInfo(
    personnel: PersonnelModel,
    args: {
      myinfo: IMyInfo;
      countries: CountryModel[];
      singpassMap: ObjectNumber;
    },
  ): void {
    const { myinfo, countries, singpassMap } = args;

    if (myinfo.sex && myinfo.sex.code) {
      const gender = mapGender(myinfo.sex.code);
      gender && (personnel.gender = gender);
    }

    if (myinfo.residentialstatus && myinfo.residentialstatus.code) {
      const residentialStatus = mapResidentialStatus(myinfo.residentialstatus.code);
      residentialStatus && (personnel.residentialStatus = residentialStatus);
    }

    if (myinfo.mobileno) {
      myinfo.mobileno.areacode?.value && (personnel.contactAreaCode = Helper.removePlusSign(myinfo.mobileno.areacode.value));
      myinfo.mobileno.nbr?.value && (personnel.contactNumber = myinfo.mobileno.nbr.value);
    }

    // if (myinfo.marital && myinfo.marital.code) {
    //   const marital = this.mapMaritalStatus(myinfo.marital.code);
    //   marital && (personnel.maritalStatus = marital);
    // }

    if (myinfo.birthcountry) {
      const country = countries.find(country => country.alpha2 === (myinfo.birthcountry?.code || 'SG'));
      country && (personnel.placeOfBirthCountryId = country.id);
    }

    if (myinfo.nationality) {
      // const nationality = singpassMap.find(nationality => nationality.sibs === (myinfo.nationality?.code || 'SG'));
      const nationality = singpassMap[myinfo.nationality.desc?.toLowerCase() || ''];
      nationality && (personnel.nationalityId = nationality);
    }

    if (myinfo.cpfcontributions?.history) {
      // personnel.cpfContributionHistory = mapCpfContributionHistory({ myinfo });
    }

    if (myinfo.noahistory?.noas) {
      // personnel.noticeOfAssessmentHistory = mapNoticeOfAssessmentHistory({ myinfo });
    }
  }
}
